import { useLocalization } from "@fluent/react";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useProductTypeContent } from "@lending/hooks/useProductTypeContent";
import { useProductSelectionContainer } from "@lending/pages/ProductSelection/hooks/useProductSelectionContainer";
import { SidebarLink, SidebarPromotion } from "./components";
import styles from "./LoanPortalSidebar.module.css";
import { useLaunchPortalApplication } from "../../hooks/useLaunchPortalApplication";
import type { LoanPortalTab } from "../../LoanPortalLayout";
import type { ProductType } from "@lending/navigation";
import type { LendingSidebarPromotion } from "@lending/types";

const SIDEBAR_PRODUCT_ICONS: Record<ProductType, string> = {
  "personal-loan": "narmi-icon-plus",
  "auto-loan": "narmi-icon-car-outline",
  "credit-card": "narmi-icon-credit-card1",
};

function LoanPortalSidebar({ activeTab }: { activeTab?: LoanPortalTab }) {
  const { l10n } = useLocalization();
  const { showAutoLoanOption, showCreditCardOption, showPersonalLoanOption } =
    useProductSelectionContainer();
  const { launchProduct, launchingProduct } = useLaunchPortalApplication();
  const productTypeContent = useProductTypeContent();
  const { institution } = useGetInstitutionQuery();

  const availableProductTypes: ProductType[] = [];

  if (showPersonalLoanOption) {
    availableProductTypes.push("personal-loan");
  }
  if (showCreditCardOption) {
    availableProductTypes.push("credit-card");
  }
  if (showAutoLoanOption) {
    availableProductTypes.push("auto-loan");
  }

  const sidebarPromotions = (institution.lending_sidebar_promotions ??
    []) as LendingSidebarPromotion[];

  return (
    <aside
      className={`${styles.sidebar} ${activeTab === "applications" ? styles.servicesHiddenOnMobile : ""}`}
    >
      <div className={styles.heading}>
        {l10n.getString(
          "loan-portal-sidebar-heading",
          null,
          "Explore more services to support you"
        )}
      </div>
      <div className={styles.list}>
        {availableProductTypes.map((productType) => (
          <SidebarLink
            key={productType}
            icon={
              <span
                className={`${SIDEBAR_PRODUCT_ICONS[productType]} ${styles.icon}`}
                aria-hidden="true"
              />
            }
            onClick={() => void launchProduct(productType)}
            title={productTypeContent[productType].name}
            subtitle={productTypeContent[productType].sidebarSubtext}
            disabled={launchingProduct !== null}
            isLoading={launchingProduct === productType}
          />
        ))}
      </div>
      {sidebarPromotions.length > 0 && (
        <div className={styles.list}>
          {sidebarPromotions.map((promotion) => (
            <SidebarPromotion
              key={`${promotion.title}-${promotion.link_url}`}
              title={promotion.title}
              body={promotion.body}
              linkText={promotion.link_text}
              linkUrl={promotion.link_url}
            />
          ))}
        </div>
      )}
    </aside>
  );
}

export { LoanPortalSidebar };
